import React, { useState } from "react";
import { UserSegmentEnum } from "../../../types";
import {
  useGetLessonViewsByTopicId,
  useGetPageViewsByTopicAndLesson,
  useGetVideoViewsByTopicId,
  useGetVideoViewsByTopicAndLesson,
} from "../../../api/queries";

export type AnalyticsItem = {
  _id: string;
  title: string;
  count: number;
  unqCount?: number;
};

interface TopicViewHierarchicalProps {
  data: AnalyticsItem[];
  title: string;
  selectedSegments: UserSegmentEnum[];
  startDate?: string;
  endDate?: string;
}

const Spinner: React.FC = () => {
  return (
    <div
      className="inline-block h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
      aria-label="Loading"
    />
  );
};

interface TopicRowProps {
  topic: AnalyticsItem;
  selectedSegments: UserSegmentEnum[];
  startDate?: string;
  endDate?: string;
}

const TopicRow: React.FC<TopicRowProps> = ({
  topic,
  selectedSegments,
  startDate,
  endDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    data: lessons = [],
    isLoading,
    isFetching,
  } = useGetLessonViewsByTopicId(
    topic._id,
    selectedSegments,
    startDate,
    endDate,
    {
      enabled: isOpen,
    },
  );

  const { data: videoViewsData } = useGetVideoViewsByTopicId(
    topic._id,
    selectedSegments,
    startDate,
    endDate,
  );

  const videoViewsByLesson = videoViewsData?.lessons ?? [];
  const totalUniqueVideoUsers = videoViewsData?.totalUniqueUsers ?? 0;

  const toggle = () => setIsOpen((prev) => !prev);

  const showSpinner = isOpen && (isLoading || isFetching);

  // Create a map of lesson ID to video views
  const videoViewsMap = videoViewsByLesson.reduce(
    (acc, item) => {
      acc[item._id] = { count: item.count, unqCount: item.unqCount };
      return acc;
    },
    {} as Record<string, { count: number; unqCount: number }>,
  );

  // Calculate total video views for the topic
  const totalVideoViews = videoViewsByLesson.reduce(
    (sum, item) => sum + item.count,
    0,
  );

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between py-2 px-2 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span
            className={`transform transition-transform ${isOpen ? "rotate-90" : ""}`}
          >
            ▶
          </span>
          <span className="font-medium text-gray-800 truncate">
            {topic.title || "Untitled Topic"}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-1">
              <span className="font-semibold">
                {topic.count.toLocaleString()}
              </span>
              <span>views</span>
            </div>
            {typeof topic.unqCount === "number" && (
              <span className="text-xs text-gray-500">
                (Users: {topic.unqCount.toLocaleString()})
              </span>
            )}
          </div>
          {totalVideoViews > 0 && (
            <div className="flex flex-col items-end border-l border-gray-300 pl-3">
              <div className="flex items-center gap-1 text-blue-600">
                <span className="font-semibold">
                  {totalVideoViews.toLocaleString()}
                </span>
                <span>video</span>
              </div>
              <span className="text-xs text-gray-500">
                (Users: {totalUniqueVideoUsers.toLocaleString()})
              </span>
            </div>
          )}
          {showSpinner && <Spinner />}
        </div>
      </button>

      {isOpen && (
        <div className="pl-6 pb-2">
          {showSpinner && lessons.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Spinner />
              <span>Loading lessons…</span>
            </div>
          ) : lessons.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">
              No lesson data available.
            </p>
          ) : (
            <div className="space-y-1">
              {lessons
                .slice()
                // Sort lessons alphabetically by title
                .sort((a, b) => {
                  const titleA = (a.title || "").toLowerCase();
                  const titleB = (b.title || "").toLowerCase();
                  return titleA.localeCompare(titleB);
                })
                .map((lesson) => (
                  <LessonRow
                    key={lesson._id}
                    topicId={topic._id}
                    lesson={lesson}
                    videoViews={videoViewsMap[lesson._id]}
                    selectedSegments={selectedSegments}
                    startDate={startDate}
                    endDate={endDate}
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface LessonRowProps {
  topicId: string;
  lesson: AnalyticsItem;
  videoViews?: { count: number; unqCount: number };
  selectedSegments: UserSegmentEnum[];
  startDate?: string;
  endDate?: string;
}

const LessonRow: React.FC<LessonRowProps> = ({
  topicId,
  lesson,
  videoViews,
  selectedSegments,
  startDate,
  endDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    data: pages = [],
    isLoading,
    isFetching,
  } = useGetPageViewsByTopicAndLesson(
    topicId,
    lesson._id,
    selectedSegments,
    startDate,
    endDate,
    {
      enabled: isOpen,
    },
  );

  const {
    data: videoViewsByPage = [],
    isLoading: isLoadingVideos,
    isFetching: isFetchingVideos,
  } = useGetVideoViewsByTopicAndLesson(
    topicId,
    lesson._id,
    selectedSegments,
    startDate,
    endDate,
    {
      enabled: isOpen,
    },
  );

  const toggle = () => setIsOpen((prev) => !prev);

  const showSpinner =
    isLoading || isFetching || isLoadingVideos || isFetchingVideos;

  // Create a map of page ID to video views
  const videoViewsMap = videoViewsByPage.reduce(
    (acc, item) => {
      acc[item._id] = { count: item.count, unqCount: item.unqCount };
      return acc;
    },
    {} as Record<string, { count: number; unqCount: number }>,
  );

  return (
    <div className="border-l border-gray-200">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between py-1.5 pl-3 pr-2 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span
            className={`transform transition-transform text-xs ${isOpen ? "rotate-90" : ""}`}
          >
            ▶
          </span>
          <span className="text-sm text-gray-800 truncate">
            {lesson.title || "Untitled Lesson"}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-1">
              <span className="font-semibold">
                {lesson.count.toLocaleString()}
              </span>
              <span>views</span>
            </div>
            {typeof lesson.unqCount === "number" && (
              <span className="text-[11px] text-gray-500">
                (Users: {lesson.unqCount.toLocaleString()})
              </span>
            )}
          </div>
          {videoViews && (
            <div className="flex flex-col items-end border-l border-gray-300 pl-3">
              <div className="flex items-center gap-1 text-blue-600">
                <span className="font-semibold">
                  {videoViews.count.toLocaleString()}
                </span>
                <span>video</span>
              </div>
              <span className="text-[11px] text-gray-500">
                (Users: {videoViews.unqCount.toLocaleString()})
              </span>
            </div>
          )}
          {showSpinner && <Spinner />}
        </div>
      </button>

      {isOpen && (
        <div className="pl-6 pb-1">
          {showSpinner && pages.length === 0 ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-1">
              <Spinner />
              <span>Loading pages…</span>
            </div>
          ) : pages.length === 0 ? (
            <p className="text-xs text-gray-500 py-1">
              No page data available.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {pages.slice().map((page) => {
                const pageVideoViews = videoViewsMap[page._id];
                return (
                  <li
                    key={page._id}
                    className="flex items-center justify-between text-xs text-gray-700 px-3 py-2 rounded-md border border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-300 transition-colors shadow-sm"
                  >
                    <span className="truncate font-medium text-gray-800">
                      {page.title || "Untitled Page"}
                    </span>
                    <span className="whitespace-nowrap font-medium flex items-center gap-3 ml-4">
                      <span className="flex items-center gap-1.5">
                        <span className="text-gray-900">
                          {page.count.toLocaleString()} views
                        </span>
                        {typeof page.unqCount === "number" && (
                          <span className="text-[11px] text-gray-500 font-normal">
                            (Users: {page.unqCount.toLocaleString()})
                          </span>
                        )}
                      </span>
                      {pageVideoViews && (
                        <span className="flex items-center gap-1.5 border-l border-gray-300 pl-3 text-blue-600">
                          <span className="font-semibold">
                            {pageVideoViews.count.toLocaleString()} video
                          </span>
                          <span className="text-[11px] text-gray-500 font-normal">
                            (Users: {pageVideoViews.unqCount.toLocaleString()})
                          </span>
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const TopicViewHierarchical: React.FC<TopicViewHierarchicalProps> = ({
  data,
  title,
  selectedSegments,
  startDate,
  endDate,
}) => {
  // Sort topics alphabetically by title
  const sortedData = [...data].sort((a, b) => {
    const titleA = (a.title || "").toLowerCase();
    const titleB = (b.title || "").toLowerCase();
    return titleA.localeCompare(titleB);
  });

  return (
    <div className="flex flex-col gap-2 p-5 w-full border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
      <h3 className="text-center m-0 mb-3 text-gray-700 text-lg underline">
        {title} (Hierarchical Views)
      </h3>
      {sortedData.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          No topic data available for the selected filters.
        </p>
      ) : (
        <div className="bg-gray-50 rounded-lg p-2 shadow-inner">
          {sortedData.map((topic) => (
            <TopicRow
              key={topic._id}
              topic={topic}
              selectedSegments={selectedSegments}
              startDate={startDate}
              endDate={endDate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TopicViewHierarchical;
